import { useMemo } from 'react';
import { PolicyActionEnum } from '@trinity/access-policy';
import { useCan } from '@trinity/access-policy/react';
import type { BrandInsightsCapabilities } from '@trinity/chat/types/capabilities';
import { useCanMarkGoodQuotes } from '@/hooks/useCanMarkGoodQuotes';
import { useWidgetLibraryCapabilities } from '@/screens/Project.ChartLibrary/hooks/useWidgetLibraryCapabilities';
import { useSegmentCapabilities } from '@/screens/Project.Segments/hooks/useSegmentCapabilities';

export const useBrandInsightsCapabilities = (): BrandInsightsCapabilities => {
  // UI gating only: chat-api strips trace links from responses for callers without this action.
  const canViewTraces = useCan(PolicyActionEnum.ChatTraceView);
  // UI gating only: PUT /projects/:projectId/segments/:segmentId/page enforces the segment edit action.
  const { canAddToDashboard: canEditSegments } = useSegmentCapabilities();
  // Adding a chart to a dashboard saves it to the project's widget library first.
  const { canAdd: canAddWidgets } = useWidgetLibraryCapabilities();
  const canAddToDashboard = canEditSegments && canAddWidgets;
  // UI gating only: POST /transcripts/:transcriptId/quotes enforces the mark-good-quotes action.
  const canMarkGoodQuotes = useCanMarkGoodQuotes();
  // UI gating only: chat-api enforces export on the chat report, chart export and table download routes,
  // and share on the share routes. Copy is client-side, so hiding the buttons is all there is to it.
  const canExportChats = useCan(PolicyActionEnum.ChatConversationExport);
  const canShareChats = useCan(PolicyActionEnum.ChatConversationShare);
  const canCopyMessages = useCan(PolicyActionEnum.ChatConversationCopy);
  // UI gating only: the project segment and patient journey list routes enforce these actions, so the
  // chat's Segment / Patient Journey filters aren't offered (and don't fetch) without them.
  const canViewSegments = useCan(PolicyActionEnum.IeSegmentView);
  const canViewPatientJourneys = useCan(PolicyActionEnum.IePatientJourneyView);

  return useMemo(
    () => ({
      canApplyFilters: false,
      canViewTraces,
      canViewExternalLinks: true,
      canAddToDashboard,
      canMarkGoodQuotes,
      canExportChats,
      canShareChats,
      canCopyMessages,
      canViewSegments,
      canViewPatientJourneys,
    }),
    [
      canViewTraces,
      canAddToDashboard,
      canMarkGoodQuotes,
      canExportChats,
      canShareChats,
      canCopyMessages,
      canViewSegments,
      canViewPatientJourneys,
    ]
  );
};
